//
// apps/vendor/src/components/ShareLinkBanner.tsx
//
// Phase 6 — Share link banner.
//
// Renders at the top of OrderShared.tsx when the viewer is looking at an
// order via a Mode B share link. Shows:
//   - "You are viewing a shared order" messaging
//   - Countdown to expiry (refreshed every 60s)
//   - Revoke button (owner only)
//
// The component receives an optional `expiresAt` / `isOwner` pair; when we
// don't know the share link id yet (the MOL-670 inline query isn't wired) we
// fall back to a generic banner with no countdown and no revoke button.

import { useEffect, useState } from "react";
import { useRevokeShareLinkMutation } from "../__generated__/graphql";
import { useConfirm } from "@clearpath/design-system";

export interface ShareLinkBannerProps {
  readonly orderId: string;
  readonly shareLinkId?: string;
  readonly expiresAt?: string;
  readonly isOwner?: boolean;
  readonly onRevoked?: () => void;
}

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return "expired";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

export function ShareLinkBanner({
  orderId,
  shareLinkId,
  expiresAt,
  isOwner,
  onRevoked,
}: ShareLinkBannerProps) {
  const [remaining, setRemaining] = useState<number>(() =>
    expiresAt ? Math.floor((Date.parse(expiresAt) - Date.now()) / 1000) : 0,
  );
  const confirm = useConfirm();

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      setRemaining(Math.floor((Date.parse(expiresAt) - Date.now()) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const [revoke, { loading: revoking }] = useRevokeShareLinkMutation();

  const canRevoke = isOwner === true && !!shareLinkId;

  return (
    <aside
      className="share-link-banner"
      role="status"
      aria-live="polite"
      style={{
        background: "#fff7e6",
        border: "1px solid #f5c16c",
        padding: "0.75rem 1rem",
        borderRadius: "0.25rem",
        marginBottom: "1rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      <div>
        <strong>You are viewing a shared order.</strong>{" "}
        {expiresAt ? (
          <span>
            Expires in {formatRemaining(remaining)} (
            <time dateTime={expiresAt}>{new Date(expiresAt).toLocaleString()}</time>
            ).
          </span>
        ) : (
          <span>Access is scoped by a share link.</span>
        )}
      </div>
      {canRevoke && (
        <button
          type="button"
          disabled={revoking}
          onClick={async () => {
            if (!shareLinkId) return;
            const ok = await confirm({
              title: "Revoke share link",
              description: "Recipients will lose access immediately. This cannot be undone.",
              intent: "destructive",
              confirmLabel: "Revoke",
            });
            if (!ok) return;
            await revoke({ variables: { id: shareLinkId } });
            onRevoked?.();
          }}
        >
          {revoking ? "Revoking…" : "Revoke link"}
        </button>
      )}
      <input
        type="hidden"
        value={orderId}
        readOnly
        data-testid="share-link-banner-order-id"
      />
    </aside>
  );
}
